import { AnimatePresence, motion } from 'framer-motion'
import { X } from 'lucide-react'
import { useCallback, useEffect } from 'react'

const layoutTransition = { type: 'spring' as const, stiffness: 350, damping: 30 }

interface MediaLightboxProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  ariaLabel: string
  layoutId?: string
  /** Width constraint class, e.g. "w-[min(92vw,1150px)]" */
  containerClassName?: string
  children: React.ReactNode
}

export function MediaLightbox({
  open,
  onOpenChange,
  ariaLabel,
  layoutId,
  containerClassName = '',
  children,
}: MediaLightboxProps) {
  const close = useCallback(() => onOpenChange(false), [onOpenChange])

  useEffect(() => {
    if (!open) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [open, close])

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-[1100] bg-black/60"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={close}
          />

          <div
            role="dialog"
            aria-label={ariaLabel}
            className="fixed inset-0 z-[1100] flex items-center justify-center pointer-events-none"
          >
            <motion.div
              className={`relative pointer-events-auto ${containerClassName}`}
              layoutId={layoutId}
              transition={layoutTransition}
              style={{ borderRadius: 12 }}
            >
              <motion.button
                className="absolute -right-3 -top-3 z-10 flex h-6 w-6 items-center justify-center rounded-full border border-white/30 bg-black/85 text-white cursor-pointer before:absolute before:-inset-2 before:content-['']"
                aria-label={`Close ${ariaLabel.toLowerCase()}`}
                onClick={close}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ delay: 0.15, duration: 0.15 }}
              >
                <X className="h-4 w-4" />
              </motion.button>
              {children}
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  )
}
